// ASK-1 + TOUR-4b + T-29 — dock query routing (catalog / tour / ask).

import type { AuthContextValue } from "@/contexts/AuthContext";
import type { PrimaryMode } from "@/components/lead/sections/LeadWorkspaceHeaderAccess";
import type { HasCapabilityFn } from "@/lib/capabilities/capabilities";
import {
  resolveFireflyAsk,
  type CanAccessPrimaryModeFn as AskCanAccessFn,
  type FireflyAskResolution,
} from "./resolveFireflyAsk";
import {
  resolveFireflyTour,
  type CanAccessPrimaryModeFn as TourCanAccessFn,
} from "./resolveFireflyTour";
import { queryPrefersFireflyCatalog } from "./fireflyCatalogExplore";
import type {
  FireflyAskArtifact,
  FireflyTourArtifact,
  FireflyTourResolution,
} from "./tourTypes";

/** Queries that prefer step-through tour matching before ask (substring on intent). */
export const FIREFLY_TOUR_WALKTHROUGH_QUERY = /\b(tour|walk me through|show me)\b/i;

export function queryPrefersTourWalkthrough(query: string): boolean {
  return FIREFLY_TOUR_WALKTHROUGH_QUERY.test(query.trim());
}

export type ResolveFireflyDockQueryInput = {
  queryText: string;
  tours: readonly FireflyTourArtifact[];
  asks: readonly FireflyAskArtifact[];
  auth: AuthContextValue;
  currentMode: PrimaryMode;
  canAccessPrimaryMode: TourCanAccessFn;
  hasCapability?: HasCapabilityFn;
};

export type FireflyDockQueryResolution =
  | { route: "catalog" }
  | { route: "tour"; tour: Extract<FireflyTourResolution, { kind: "start" }> }
  | { route: "ask"; ask: FireflyAskResolution };

export function resolveFireflyDockQuery(
  input: ResolveFireflyDockQueryInput,
): FireflyDockQueryResolution {
  // Catalog explore phrases include "show me" — check before walkthrough routing.
  if (queryPrefersFireflyCatalog(input.queryText)) {
    return { route: "catalog" };
  }

  const canAccess = input.canAccessPrimaryMode as AskCanAccessFn;

  const runTour = (): FireflyTourResolution =>
    resolveFireflyTour({
      queryText: input.queryText,
      artifacts: input.tours,
      auth: input.auth,
      currentMode: input.currentMode,
      canAccessPrimaryMode: input.canAccessPrimaryMode,
      hasCapability: input.hasCapability,
    });

  const runAsk = (): FireflyAskResolution =>
    resolveFireflyAsk({
      queryText: input.queryText,
      artifacts: input.asks,
      auth: input.auth,
      currentMode: input.currentMode,
      canAccessPrimaryMode: canAccess,
    });

  if (queryPrefersTourWalkthrough(input.queryText)) {
    const tour = runTour();
    if (tour.kind === "start") return { route: "tour", tour };
    if (tour.kind === "denied" && tour.reason === "no_access") {
      return {
        route: "ask",
        ask: {
          kind: "denied",
          artifactId: "tour-denied",
          targetMode: tour.targetMode ?? input.currentMode,
          message:
            tour.message ?? "You don't have access to that tour from this seat.",
        },
      };
    }
    return { route: "ask", ask: runAsk() };
  }

  const ask = runAsk();
  if (ask.kind !== "not_found") return { route: "ask", ask };

  const tour = runTour();
  if (tour.kind === "start") return { route: "tour", tour };
  return { route: "ask", ask };
}
